import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  Chip,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import Input from "../../common/InputField";
import DeletePopUp from "../DeletePopUp";
import {
  fetchMailDomainAuth,
  registerMailDomain,
  validateMailDomain,
  deleteMailDomainAuth,
  clearValidateMessage,
} from "../../../store/features/mailDomainAuth";
import "./style.css";

const mono = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 13,
  color: "#061522",
  wordBreak: "break-all",
};

const formHeadingSx = {
  color: "#061522",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 500,
  lineHeight: "22px",
  mb: 1.5,
};

const formBodySx = {
  color: "#405261",
  fontFamily: "Inter",
  fontSize: "14px",
  fontWeight: 400,
  lineHeight: "22px",
};

const removeBtnSx = {
  textTransform: "none",
  fontFamily: "Inter",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "6px",
  borderColor: "#FF4D4F",
  color: "#FF4D4F",
  px: 1.5,
  py: 0.75,
  minWidth: 0,
  "&:hover": {
    borderColor: "#DC2626",
    color: "#DC2626",
    bgcolor: "rgba(255, 77, 79, 0.06)",
  },
  "&.Mui-disabled": { borderColor: "#F5C6C7", color: "#F5A8AA" },
};

function getErrorMessage(payload) {
  if (!payload) return "Something went wrong";
  if (typeof payload === "string") return payload;
  return (
    payload.message ||
    payload.error ||
    (Array.isArray(payload.errors) && payload.errors[0]?.message) ||
    "Something went wrong"
  );
}

function isValidMailDomainInput(value) {
  const d = String(value || "").trim().toLowerCase();
  if (!d || d.length > 253) return false;
  if (d.includes("..") || d.startsWith(".") || d.endsWith(".")) return false;
  const labels = d.split(".");
  if (labels.length < 2) return false;
  for (let i = 0; i < labels.length; i += 1) {
    const label = labels[i];
    if (!label || label.length > 63) return false;
    if (label.startsWith("-") || label.endsWith("-")) return false;
    if (!/^[a-z0-9-]+$/.test(label)) return false;
  }
  const tld = labels[labels.length - 1];
  return /^[a-z]{2,}$/.test(tld);
}

function recordErrorText(record) {
  if (record.reason != null && String(record.reason).trim() !== "") {
    return String(record.reason);
  }
  return "This record was not found at your DNS provider. Add it exactly as shown, then validate again.";
}

function domainVerifiedUserMessage(raw) {
  if (typeof raw !== "string" || raw.trim() === "") {
    return "Domain verified.";
  }
  const t = raw.trim();
  if (!t.toLowerCase().startsWith("domain verified")) {
    return "Domain verified.";
  }
  const dot = t.indexOf(".");
  return dot >= 0 ? t.slice(0, dot + 1).trim() : "Domain verified.";
}

function DomainPanelSkeleton() {
  return (
    <Box>
      <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" width={120} height={28} sx={{ mb: 3 }} />
      <Skeleton variant="rounded" width="100%" height={44} sx={{ mb: 2 }} />
      <Skeleton variant="rounded" width={128} height={40} />
    </Box>
  );
}

function DnsTableSkeleton() {
  return (
    <Box sx={{ mt: 2 }}>
      <Skeleton variant="text" width="90%" height={18} sx={{ mb: 2 }} />
      <Box
        sx={{
          border: "1px solid #E5E9EC",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        {[1, 2, 3].map((k) => (
          <Skeleton
            key={k}
            variant="rounded"
            height={48}
            sx={{ m: 1, borderRadius: "4px" }}
          />
        ))}
      </Box>
    </Box>
  );
}

function CopyIconBtn({ text, label }) {
  return (
    <Tooltip title={label || "Copy"}>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          navigator.clipboard.writeText(text);
          toast.success("Copied");
        }}
        sx={{
          p: '0px 2.8px 2.8px',
          flexShrink: 0,
          color: "#93A1B0",
          "&:hover": { color: "#148DFD" },
        }}
      >
        <ContentCopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
}

function DnsRecordBlock({ record }) {
  const ok = Boolean(record.valid);
  const typeStr = (record.type || "").toUpperCase();

  return (
    <Box
      sx={{
        borderTop: "1px solid #E5E9EC",
        "&:first-of-type": { borderTop: "none" },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "32px 88px minmax(0, 1fr) minmax(0, 1fr)",
          columnGap: 1.25,
          alignItems: "center",
          px: 1.5,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          {ok ? (
            <CheckCircleIcon sx={{ color: "#22C55E", fontSize: 20 }} />
          ) : (
            <CloseIcon sx={{ color: "#DC2626", fontSize: 20 }} />
          )}
        </Box>

        <Box>
          <Typography sx={{ ...mono, fontSize: 12, fontWeight: 600 }}>
            {typeStr}
          </Typography>
          {record.key ? (
            <Typography
              sx={{
                fontFamily: "Inter",
                fontSize: 11,
                color: "#93A1B0",
                mt: 0.25,
              }}
            >
              {record.key}
            </Typography>
          ) : null}
        </Box>

        <Box
          sx={{
            display: "inline-flex",
            alignItems: "flex-start",
            maxWidth: "100%",
            minWidth: 0,
            gap: 0.5,
            justifySelf: "start",
          }}
        >
          <Typography
            component="span"
            sx={{
              ...mono,
              minWidth: 0,
              wordBreak: "break-all",
              lineHeight: 1.45,
            }}
          >
            {record.host}
          </Typography>
          <Box sx={{ flexShrink: 0, lineHeight: 0, pt: "2px" }}>
            <CopyIconBtn text={record.host} label="Copy name" />
          </Box>
        </Box>

        <Box
          sx={{
            display: "inline-flex",
            alignItems: "flex-start",
            maxWidth: "100%",
            minWidth: 0,
            gap: 0.5,
            justifySelf: "start",
          }}
        >
          <Typography
            component="span"
            sx={{
              ...mono,
              minWidth: 0,
              wordBreak: "break-all",
              lineHeight: 1.45,
            }}
          >
            {record.data}
          </Typography>
          <Box sx={{ flexShrink: 0, lineHeight: 0, pt: "2px" }}>
            <CopyIconBtn text={record.data} label="Copy value" />
          </Box>
        </Box>
      </Box>

      {!ok && (
        <Box
          sx={{
            px: 1.5,
            pt: 1.25,
            pb: 1.5,
            borderTop: "1px solid #FEE2E2",
            bgcolor: "#FFFBFB",
          }}
        >
          {/* <Chip
            label="Invalid Configuration"
            size="small"
            sx={{
              height: 24,
              fontFamily: "Inter",
              fontSize: 11,
              fontWeight: 600,
              bgcolor: "#FEE2E2",
              color: "#B91C1C",
              border: "1px solid #FECACA",
              "& .MuiChip-label": { px: 1 },
              mb: 1,
            }}
          /> */}
          <Typography
            sx={{
              fontFamily: "Inter",
              fontSize: 13,
              color: "#991B1B",
              lineHeight: 1.5,
            }}
          >
            {recordErrorText(record)}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function CustomBusinessDomain() {
  const dispatch = useDispatch();
  const roleName = useSelector(
    (state) => state.loginToken?.response?.user?.role_name,
  );
  const isAdminWithBilling = roleName === "admin_with_billing";
  const [domainInput, setDomainInput] = useState("");
  const [deletePopupOpen, setDeletePopupOpen] = useState(false);

  const {
    data,
    fetchLoading,
    registerLoading,
    validateLoading,
    deleteLoading,
    validateMessage,
  } = useSelector((state) => state.mailDomainAuth);

  useEffect(() => {
    if (!isAdminWithBilling) return undefined;
    dispatch(fetchMailDomainAuth()).then((action) => {
      if (
        fetchMailDomainAuth.rejected.match(action) &&
        !action.payload?.notFound
      ) {
        toast.error(getErrorMessage(action.payload));
      }
    });
    return undefined;
  }, [dispatch, isAdminWithBilling]);

  const registered = Boolean(data?.registered);
  const verified = Boolean(data?.mail_domain_verified);
  const dnsRecords = Array.isArray(data?.dns) ? data.dns : [];
  const anyRecordInvalid = dnsRecords.some((r) => !r.valid);
  const configInvalid = !verified || anyRecordInvalid;
  const dnsFullyVerified = registered && verified && !anyRecordInvalid;

  const handleRegister = async () => {
    const d = domainInput.trim().toLowerCase();
    if (!d) {
      toast.error("Enter a domain");
      return;
    }
    if (!isValidMailDomainInput(domainInput)) {
      toast.error("Enter a valid domain (e.g. mail.example.com)");
      return;
    }
    dispatch(clearValidateMessage());
    try {
      await dispatch(registerMailDomain({ domain: d })).unwrap();
      toast.success("Domain added");
      setDomainInput("");
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleValidate = async () => {
    dispatch(clearValidateMessage());
    try {
      const res = await dispatch(validateMailDomain()).unwrap();
      await dispatch(fetchMailDomainAuth()).unwrap();
      const msg = res?.message;
      const toastMsg =
        typeof msg === "string" &&
        msg.trim().toLowerCase().startsWith("domain verified")
          ? domainVerifiedUserMessage(msg)
          : msg || "DNS updated";
      toast.success(toastMsg);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const confirmDeleteDomain = async () => {
    if (deleteLoading) return;
    dispatch(clearValidateMessage());
    try {
      await dispatch(deleteMailDomainAuth()).unwrap();
      toast.success("Domain removed");
      setDeletePopupOpen(false);
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  const handleRefresh = () => {
    dispatch(clearValidateMessage());
    dispatch(fetchMailDomainAuth());
  };

  const showInitialSkeleton = fetchLoading && !data;
  const domainName = data?.mail_auth_domain ?? "—";
  const useFullWidthContentColumn = registered;

  if (!isAdminWithBilling) {
    return (
      <div className="profile-main-container">
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography
            sx={{
              fontFamily: "Inter",
              fontSize: 14,
              color: "#991B1B",
              lineHeight: 1.5,
            }}
          >
            Only an admin with billing access can add or manage the custom mail
            domain for your organization.
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div className="profile-main-container">
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: "flex-start",
          p: { xs: 2, md: 3 },
          gap: { xs: 4, md: 2 },
          width: "100%",
        }}
      >
        <Box
          sx={{ maxWidth: { xs: "100%", md: 300 } }}
          className="Personal-Info-Text-Container"
        >
          <Typography
            sx={{
              color: "#061522",
              fontFamily: "Inter",
              fontSize: { xs: "16px", md: "18px" },
              fontWeight: 500,
              lineHeight: "22px",
              mb: 1,
            }}
          >
            Custom Domain
          </Typography>
          <Typography
            sx={{
              color: "#405261",
              fontFamily: "Inter",
              fontSize: { xs: "13px", md: "14px" },
              fontWeight: 400,
              lineHeight: "22px",
            }}
          >
            Match these DNS records at your provider, then validate.
          </Typography>
        </Box>

        <Box
          sx={
            useFullWidthContentColumn
              ? { flex: 1, minWidth: 0, width: "100%" }
              : { width: { xs: "100%", md: "552px" }, pr: { md: 7 } }
          }
        >
          {showInitialSkeleton ? (
            <DomainPanelSkeleton />
          ) : (
            <>
              {!registered ? (
                <>
                  <Typography sx={formHeadingSx}>Add your mail domain</Typography>
                  <Typography sx={{ ...formBodySx, mb: 2 }}>
                    Enter the domain you use for outbound ticket email. After you
                    add it, you will configure DNS records and validate.
                  </Typography>
                  <div className="Spacing-Bottom">
                    <Input
                      title="Domain"
                      placeholder="trustchat.com"
                      value={domainInput}
                      onChange={(e) => setDomainInput(e.target.value)}
                    />
                  </div>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      width: "100%",
                      mt: 2.5,
                    }}
                  >
                    <button
                      style={{ marginTop: 0, float: "none" }}
                      className={`Personal-Information-Save-Button${
                        registerLoading
                          ? " Personal-Information-Save-Button--primary-loading"
                          : ""
                      }`}
                      type="button"
                      onClick={handleRegister}
                      disabled={
                        registerLoading ||
                        !isValidMailDomainInput(domainInput)
                      }
                    >
                      {registerLoading ? (
                        <CircularProgress size={22} sx={{ color: "#fff" }} />
                      ) : (
                        "Add domain"
                      )}
                    </button>
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    border: "1px solid #E5E9EC",
                    borderRadius: "10px",
                    bgcolor: "#FFFFFF",
                    overflow: "hidden",
                  }}
                >
                  {/* Header — Vercel-style */}
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                      gap: 2,
                      px: 2,
                      py: 2,
                      borderBottom: "1px solid #E5E9EC",
                    }}
                  >
                    <Box sx={{ display: "flex", gap: 1.25, flex: 1, minWidth: 200 }}>
                      {configInvalid ? (
                        <ReportProblemIcon
                          sx={{ color: "#DC2626", fontSize: 22, mt: 0.25 }}
                        />
                      ) : (
                        <CheckCircleIcon
                          sx={{ color: "#22C55E", fontSize: 22, mt: 0.25 }}
                        />
                      )}
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "Inter",
                            fontSize: 18,
                            fontWeight: 600,
                            color: "#061522",
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {domainName}
                        </Typography>
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1, mt: 0.75 }}>
                          <Chip
                            label={
                              configInvalid
                                ? "Invalid Configuration"
                                : "Valid Configuration"
                            }
                            size="small"
                            sx={{
                              height: 26,
                              fontFamily: "Inter",
                              fontSize: 12,
                              fontWeight: 600,
                              bgcolor: configInvalid ? "#FEE2E2" : "#DCFCE7",
                              color: configInvalid ? "#B91C1C" : "#15803D",
                              border: `1px solid ${configInvalid ? "#FECACA" : "#BBF7D0"}`,
                              "& .MuiChip-label": { px: 1.25 },
                            }}
                          />
                          {data?.ticket_reply_host ? (
                            <Typography
                              sx={{
                                fontFamily: "Inter",
                                fontSize: 12,
                                color: "#93A1B0",
                              }}
                            >
                              Reply host:{" "}
                              <Box component="span" sx={{ color: "#405261", fontFamily: mono.fontFamily }}>
                                {data.ticket_reply_host}
                              </Box>
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1,
                        ml: { xs: 0, md: "auto" },
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontSize: 11,
                          fontWeight: 600,
                          color: "#93A1B0",
                          textTransform: "uppercase",
                          letterSpacing: "0.06em",
                          mr: { xs: 0, sm: 0.5 },
                        }}
                      >
                        Mail
                      </Typography>
                      <Tooltip title="Refresh">
                        <span>
                          <IconButton
                            onClick={handleRefresh}
                            disabled={fetchLoading}
                            size="small"
                            sx={{
                              border: "1px solid #D5DCE1",
                              borderRadius: "6px",
                              color: "#405261",
                              p: 0.65,
                              "&:hover": {
                                bgcolor: "#FAFBFC",
                                borderColor: "#C5CED6",
                              },
                              "&.Mui-disabled": {
                                borderColor: "#E5E9EC",
                                color: "#C5CED6",
                              },
                            }}
                          >
                            <RefreshIcon sx={{ fontSize: 20 }} />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Button
                        variant="outlined"
                        onClick={() => setDeletePopupOpen(true)}
                        disabled={deleteLoading || validateLoading}
                        sx={removeBtnSx}
                      >
                        Remove
                      </Button>
                    </Box>
                  </Box>

                  {/* Tab strip */}
                  <Box sx={{ px: 2, pt: 1.5, borderBottom: "1px solid #E5E9EC" }}>
                    <Typography
                      sx={{
                        display: "inline-block",
                        fontFamily: "Inter",
                        fontSize: 14,
                        fontWeight: 600,
                        color: "#061522",
                        pb: 1.25,
                        borderBottom: "2px solid #061522",
                        mb: "-1px",
                      }}
                    >
                      DNS Records
                    </Typography>
                  </Box>

                  <Box sx={{ px: 2, py: 2 }}>
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontSize: 14,
                        color: "#405261",
                        lineHeight: 1.55,
                        mb: 2,
                      }}
                    >
                      The DNS records at your provider must match the following
                      records to verify and connect mail for your domain.
                    </Typography>

                    {/* {dnsFullyVerified ? (
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.5,
                          borderRadius: "8px",
                          border: "1px solid #BBF7D0",
                          bgcolor: "#F0FDF4",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "Inter",
                            fontSize: 13,
                            color: "#15803D",
                            lineHeight: 1.5,
                            fontWeight: 500,
                          }}
                        >
                          {domainVerifiedUserMessage(validateMessage)}
                        </Typography>
                      </Box>
                    ) : null} */}

                    {fetchLoading && data ? (
                      <DnsTableSkeleton />
                    ) : dnsRecords.length === 0 ? (
                      <Typography
                        sx={{
                          color: "#93A1B0",
                          fontFamily: "Inter",
                          fontSize: 13,
                          py: 2,
                        }}
                      >
                        No records
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          border: "1px solid #E5E9EC",
                          borderRadius: "8px",
                          overflow: "hidden",
                        }}
                      >
                        <Box sx={{ width: "100%", minWidth: 0 }}>
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "32px 88px minmax(0, 1fr) minmax(0, 1fr)",
                                columnGap: 1.25,
                                alignItems: "center",
                                px: 1.5,
                                py: 1.25,
                                bgcolor: "#FAFBFC",
                                borderBottom: "1px solid #E5E9EC",
                              }}
                            >
                              <Box />
                              <Typography
                                sx={{
                                  fontFamily: "Inter",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#93A1B0",
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                }}
                              >
                                Type
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Inter",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#93A1B0",
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                }}
                              >
                                Name
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: "Inter",
                                  fontSize: 11,
                                  fontWeight: 600,
                                  color: "#93A1B0",
                                  letterSpacing: "0.04em",
                                  textTransform: "uppercase",
                                }}
                              >
                                Value
                              </Typography>
                            </Box>
                            {dnsRecords.map((rec, i) => (
                              <DnsRecordBlock
                                key={`${rec.key || rec.host}-${i}`}
                                record={rec}
                              />
                            ))}
                        </Box>
                      </Box>
                    )}

                    <Box
                      sx={{
                        mt: 2,
                        p: 1.5,
                        borderRadius: "8px",
                        border: "1px solid #E5E9EC",
                        bgcolor: "#FAFBFC",
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "Inter",
                          fontSize: 13,
                          color: "#405261",
                          lineHeight: 1.5,
                        }}
                      >
                        For ticket replies by email, add an{" "}
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          MX
                        </Box>{" "}
                        record to{" "}
                        <Box component="span" sx={{ fontFamily: mono.fontFamily }}>
                          mx.sendgrid.net
                        </Box>{" "}
                        on your reply host when your validation message asks for
                        it.
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                      }}
                    >
                      <button
                        style={{ marginTop: 0, float: "none" }}
                        className={`Personal-Information-Save-Button${
                          validateLoading
                            ? " Personal-Information-Save-Button--primary-loading"
                            : ""
                        }`}
                        type="button"
                        onClick={handleValidate}
                        disabled={
                          validateLoading ||
                          deleteLoading ||
                          dnsFullyVerified
                        }
                      >
                        {validateLoading ? (
                          <CircularProgress size={22} sx={{ color: "#fff" }} />
                        ) : (
                          "Validate DNS"
                        )}
                      </button>
                    </Box>
                  </Box>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
      {deletePopupOpen ? (
        <DeletePopUp
          onCancel={() => setDeletePopupOpen(false)}
          onDelete={confirmDeleteDomain}
        />
      ) : null}
    </div>
  );
}

export default CustomBusinessDomain;
